import { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLazyGetOrganizationsQuery } from "reduxFiles/api/companyApi";
import {
  useCreateFormMutation,
  useDeleteFormMutation,
  useGetFormsQuery,
  useUpdateFormMutation,
} from "reduxFiles/api/reportAndFormApi";
import { Column, TableAction } from "types/globalTypes";
import {
  FormQuestionOption,
  FormQuestionType,
  FormsType,
} from "types/reportTypes";
import FormModal from "./FormModal";
import { OptionKey } from "types/assessmentTypes";

const Forms = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // states
  const [item, setItem] = useState<FormsType | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetFormsQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateFormMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdateFormMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteFormMutation();

  // search organizations
  const [triggerOrganizations] = useLazyGetOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form and table
  const fields: Field[] = [
    {
      name: "title",
      label: t("forms.name"),
      type: "text",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "organizationId",
      label: t("organization.name"),
      type: "select",
      onSearch: searchOrganizations,
      filtered: true,
    },
    {
      name: "items",
      label: t("forms.items"),
      type: "array",
      formed: true,
      required: true,
      arrayFields: [
        {
          name: "name",
          label: t("forms.questionName"),
          type: "text",
          required: true,
          gridCol: 4,
        },
        {
          name: "index",
          label: t("forms.questionIndex"),
          type: "number",
          required: true,
        },
        {
          name: "group",
          label: t("forms.questionGroup"),
          type: "text",
          required: true,
          gridCol: 2,
        },
        {
          name: "key",
          label: t("forms.questionKey"),
          type: "text",
          required: true,
          gridCol: 2,
        },
        {
          name: "factor",
          label: t("forms.questionFactor"),
          type: "number",
          required: true,
        },
        {
          name: "reverse",
          label: t("forms.reverse"),
          type: "checkbox",
        },
      ],
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<FormsType>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "title",
      title: t("forms.name"),
    },
    {
      key: "items",
      title: t("forms.items"),
      render: (row) => row.items?.length,
    },
    {
      key: "organizations",
      title: t("forms.orgs"),
      render: (row) => row.organizations?.length || "0",
    },
  ];

  const actions: TableAction<FormsType>[] = [
    {
      label: t("forms.editForm"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("forms.deleteForm"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  const options: Partial<FormQuestionOption>[] = [
    {
      name: "STRONGLY_DISAGREE",
      key: OptionKey.STRONGLY_DISAGREE,
      value: 1,
    },
    {
      name: "DISAGREE",
      key: OptionKey.DISAGREE,
      value: 2,
    },
    {
      name: "NEUTRAL",
      key: OptionKey.NEUTRAL,
      value: 3,
    },
    {
      name: "AGREE",
      key: OptionKey.AGREE,
      value: 4,
    },
    {
      name: "STRONGLY_AGREE",
      key: OptionKey.STRONGLY_AGREE,
      value: 5,
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    const body: { title: string; items: FormQuestionType[] } = {
      title: formData.title,
      items: formData.items?.map((f: FormQuestionType) => ({
        ...(item ? { id: f.id } : {}),
        factor: Number(f.factor),
        index: Number(f.index),
        name: f.name,
        group: f.group,
        key: f.key,
        options:
          item && f?.options?.length > 0
            ? f.options?.map((c) => ({
                ...c,
                value: f.reverse ? 6 - c.value : c.value,
              }))
            : options.map((o) => ({
                ...o,
                value: f.reverse ? 6 - o.value : o.value,
              })),
      })),
    };

    try {
      if (item) {
        await updateApi({ id: item.id, data: body }).unwrap();
      } else {
        await addApi(body).unwrap();
      }
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("forms.title")}
          createBtn={{
            title: t("forms.addForm"),
            handler: () => modal.openModal(),
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<FormsType>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <FormModal
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={item ? item : {}}
          title={item ? t("forms.editForm") : t("forms.addForm")}
          isEdit={!!item}
          loading={item ? isUpdating : isAdding}
          maxWidth="md:max-w-[600px] lg:max-w-[750px]"
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("forms.deleteForm")}
          desc={t("actions.confirmDeleteDesc", { name: item?.title || "" })}
        />
      </div>
    </div>
  );
};

export default Forms;
